/**
 * Agent bridge client -- wire-compatible with the prototype's AGENTS.md
 * contract: push a state snapshot every 400 ms, drain and execute queued
 * commands. Works against server.py today and the .NET API in Phase 3.
 */
import { SPEEDS } from '../data/balance.js';
import type { Game } from '../core/game.js';
import { simSnapshot } from '../core/snapshot.js';
import { applyReplayAction } from '../core/replay.js';
import type { ReplayAction } from '../core/types.js';

export interface BridgeHost {
  game(): Game;
  replayMode(): boolean;
  desynced(): boolean;
  speedIdx(): number;
  setSpeed(idx: number): void;
  watchReplay(id: string): void;
  restart(): void;
  /** Called when an agent issued at least one command this tick. */
  onAgentActive(): void;
}

interface BridgeCommand {
  readonly op: string;
  readonly x?: number;
  readonly y?: number;
  readonly x2?: number;
  readonly y2?: number;
  readonly i?: number;
  readonly n?: number;
  readonly key?: string;
  readonly sk?: string;
  readonly idx?: number;
  readonly id?: string;
}

const SIM_OPS = new Set([
  'place',
  'pickup',
  'keep',
  'combine',
  'recipe',
  'upgrade',
  'lift',
  'drop',
  'slate',
  'slateUp',
  'retire',
]);
const INTERVAL_MS = 400;

export class BridgeClient {
  private readonly session: string;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly host: BridgeHost) {
    this.session = new URLSearchParams(location.search).get('session') ?? 'local';
  }

  start(): void {
    this.timer ??= setInterval(() => {
      void this.tick();
    }, INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private async tick(): Promise<void> {
    try {
      const game = this.host.game();
      const snapshot = {
        ...simSnapshot(game),
        speed: SPEEDS[this.host.speedIdx()],
        replayMode: this.host.replayMode(),
        desynced: this.host.desynced(),
      };
      await fetch(`/api/state?s=${this.session}`, {
        method: 'POST',
        body: JSON.stringify(snapshot),
      });
      const res = await fetch(`/api/cmd?s=${this.session}`);
      const cmds: unknown = await res.json();
      if (Array.isArray(cmds) && cmds.length) {
        for (const c of cmds as BridgeCommand[]) this.exec(c);
        this.host.onAgentActive();
      }
    } catch {
      // no bridge server running: UI-only mode (prototype behavior)
    }
  }

  private exec(c: BridgeCommand): void {
    const game = this.host.game();
    if (this.host.replayMode() && SIM_OPS.has(c.op)) return;
    if (SIM_OPS.has(c.op)) {
      // Prototype agentExec parity: combine/recipe are gated so a stale or
      // malformed agent command can't reach the sim with an invalid index.
      if (
        (c.op === 'combine' || c.op === 'recipe' || c.op === 'slate') &&
        !(game.phase === 'choose' && c.i !== undefined && game.newGems[c.i])
      ) {
        return;
      }
      if (!game.recording.result) game.markAgentPlayer();
      applyReplayAction(game, c as unknown as ReplayAction);
      return;
    }
    switch (c.op) {
      case 'speed':
        this.host.setSpeed(Math.max(0, Math.min(SPEEDS.length - 1, c.idx ?? 0)));
        break;
      case 'watchReplay':
        if (typeof c.id === 'string') this.host.watchReplay(c.id);
        break;
      case 'restart':
        this.host.restart();
        break;
    }
  }
}
